import { IconName, MenuOption } from '@focaldata/cin-ui-components'
import React, { memo, useState } from 'react'
import { useAppSelector } from '../../../App.store'
import { questionBeingEditedId } from '../../../apollo/apolloClient'
import { emptyArr } from '../../../constants/misc'
import { ForkTag } from '../../../data/gql-gen/questionnaire/graphql'
import { Operator } from '../../../data/model/common'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  QuestionTypeCode,
  SettingValue
} from '../../../data/model/questionnaire'
import { IsNonManualUIChange } from '../../../hooks/copyPaste/useCopyPasteComplete'
import { useEmptyQuestionCardMutation } from '../../../hooks/questionnaire/useEmptyQuestionCard'
import { useSetLoopSpecificDisplayLogic } from '../../../hooks/questionnaire/useLoopSpecificDisplayLogic/useLoopSpecificDisplayLogic.mutations'
import useQuestionLogic from '../../../hooks/questionnaire/useQuestionLogic'
import { ClauseBlockContainer } from '../../../utils/questionLogic'
import {
  getLetterPrefix,
  getQuestionType
} from '../../../utils/questionnaireUtils'
import DisplayLogicHeader from '../DisplayLogicHeader'
import { selectQuestionLogicByQuestionId } from '../Questionnaire.slice'
import {
  hasReferenceableAudienceQuestion,
  useSharedAudienceQuestions
} from '../audienceDisplayLogic'
import DialogAddDisplayLogic from './DialogAddDisplayLogic'
import DialogAddLoopSpecificDisplayLogic from './DialogAddLoopSpecificDisplayLogic'
import IdContent, { QuestionTypeString } from './IdContent'
import SurveyQuestionCardHeader from './SurveyQuestionCardHeader.control'

export enum QuestionType {
  SingleChoice,
  MultipleChoice,
  Ranked,
  Scale,
  FreeText,
  Matrix
}

interface Props {
  entry: DraftQuestionnaireEntry
  hasError?: boolean
  isMultipleChoice?: boolean
  disabledDuplicate?: boolean
  entries?: DraftQuestionnaireEntry[]
  titleContent?: JSX.Element
  onQuestionTypeChange?: (settingValue: SettingValue) => void
  onClickDuplicateIcon?: () => void
}

const SurveyQuestionCardHeaderContainer: React.FC<Props> = (props: Props) => {
  const {
    entry,
    hasError = false,
    isMultipleChoice = false,
    disabledDuplicate,
    entries = emptyArr,
    titleContent,
    onClickDuplicateIcon,
    onQuestionTypeChange
  } = props
  const [displayLogicDialogOpen, setDisplayLogicDialogOpen] = useState(false)
  const [
    loopSpecificDisplayLogicDialogOpen,
    setLoopSpecificDisplayLogicDialogOpen
  ] = useState(false)
  const { setQuestionLogic, removeLogic, loading } = useQuestionLogic(entry.id)
  const setLoopSpecificDisplayLogic = useSetLoopSpecificDisplayLogic(entry.id)
  const questionCardType = getQuestionType(entry)
  const questionCodeMap: Map<QuestionTypeCode, QuestionTypeString> = new Map<
    QuestionTypeCode,
    QuestionTypeString
  >([
    [
      QuestionTypeCode.Basic,
      isMultipleChoice ? 'Multi select' : 'Single select'
    ],
    [QuestionTypeCode.Ranked, 'Ranked'],
    [QuestionTypeCode.FreeText, 'Free text'],
    [QuestionTypeCode.Scale, 'Slider'],
    [QuestionTypeCode.TextCard, 'Text'],
    [QuestionTypeCode.Matrix, 'Matrix'],
    [QuestionTypeCode.Section, 'Section'],
    [QuestionTypeCode.MaxDiff, 'Max Diff'],
    [
      QuestionTypeCode.TextHighlighter,
      isMultipleChoice
        ? 'Text highlighter - multi select'
        : 'Text highlighter - single select'
    ],
    [QuestionTypeCode.Conjoint, 'Conjoint']
  ])

  const { id: entryId, position: entryPosition, entryType, sectionId } = entry
  const entryItem = entry.entryItem as DraftQuestionItem
  const entryMatrixItem = entry.entryItem as DraftMatrixItem
  const questionLogic =
    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    useAppSelector((state) =>
      selectQuestionLogicByQuestionId(state, entryId)
    ) || entryItem.questionLogic

  // @todo Legacy eslint violation – fix this when editing
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const position = entryPosition ?? 0
  // @todo Legacy eslint violation – fix this when editing
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const contextPosition = entry.contextPosition ?? 0
  const { forks } = entry
  const typeName = questionCodeMap.get(questionCardType)
  const letterPrefix = getLetterPrefix(entryType)
  const showMenu = [
    QuestionTypeCode.Basic,
    QuestionTypeCode.TextHighlighter
  ].includes(questionCardType)
  // The first question can only carry display logic if there is an audience
  // question to reference, and multi-market projects can only reference the
  // ones every market shares — so the audience is read for that card alone.
  const isFirstEntry = position === 0
  const { isMultiMarketV2, sharedAudienceQuestionIds } =
    useSharedAudienceQuestions({ skip: !isFirstEntry })
  const surveyHasAudienceQuestions = hasReferenceableAudienceQuestion(
    entries,
    isMultiMarketV2,
    sharedAudienceQuestionIds
  )
  const { emptyQuestionCardMutation, emptyMatrixQuestionMutation } =
    useEmptyQuestionCardMutation()

  const handleEmptyQuestionCard = async () => {
    IsNonManualUIChange(true)
    if (
      [QuestionTypeCode.Basic, QuestionTypeCode.Ranked].includes(
        questionCardType
      )
    )
      await emptyQuestionCardMutation(entryItem.questionLk)
    else if (questionCardType === QuestionTypeCode.Matrix) {
      await emptyMatrixQuestionMutation(entryMatrixItem.matrixTitleLk)
    }
  }

  const handleRemoveLoopBasedLogic: () => void = () => {
    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (entry) {
      questionBeingEditedId(entry.id)
      setLoopSpecificDisplayLogic(null)
    }
  }
  const simpleMenu: MenuOption[] = [
    {
      id: 0,
      options: [
        {
          text: 'Single select',
          onClick: () => {
            if (onQuestionTypeChange) {
              onQuestionTypeChange(SettingValue.SingleChoice)
            }
          },
          iconName: IconName.SingleChoice
        },
        {
          text: 'Multi select',
          onClick: () => {
            if (onQuestionTypeChange) {
              onQuestionTypeChange(SettingValue.MultipleChoice)
            }
          },
          iconName: IconName.MultipleChoice
        }
      ]
    }
  ]
  const handleOnSave: (
    entry: DraftQuestionnaireEntry | undefined,
    clauseBlockContainers: ClauseBlockContainer[],
    operator: Operator,
    forkTags: ForkTag[]
  ) => void = async (entry, clauseBlockContainers, operator, forkTags) => {
    if (entry) {
      const clauseBlocks = clauseBlockContainers.flatMap(
        (container) => container.clauseBlocks
      )

      await setQuestionLogic(clauseBlocks, operator, forkTags)
    }
    if (!loading) {
      setDisplayLogicDialogOpen(false)
    }
  }

  return (
    <>
      <SurveyQuestionCardHeader
        entry={entry}
        disabledDuplicate={disabledDuplicate}
        disableDisplayLogic={entryPosition === 0 && !surveyHasAudienceQuestions}
        onClickDuplicateIcon={onClickDuplicateIcon}
        onClickAddDisplayLogic={() => {
          setDisplayLogicDialogOpen(true)
        }}
        onClickAddLoopSpecificDisplayLogic={() => {
          setLoopSpecificDisplayLogicDialogOpen(true)
        }}
        onEmptyQuestionCard={handleEmptyQuestionCard}
        idContent={
          <IdContent
            hasError={hasError}
            letterPrefix={letterPrefix}
            showMenu={showMenu}
            typeName={typeName}
            simpleMenu={simpleMenu}
            contextPosition={contextPosition}
          />
        }
        logicContent={
          <DisplayLogicHeader
            questionLogic={questionLogic}
            entry={entry}
            forks={forks}
            entries={entries}
            onEditDisplayLogic={() => {
              setDisplayLogicDialogOpen(!displayLogicDialogOpen)
            }}
            onRemoveDisplayLogic={removeLogic}
            onEditLoopBasedDisplayLogic={() => {
              setLoopSpecificDisplayLogicDialogOpen(
                !loopSpecificDisplayLogicDialogOpen
              )
            }}
            onRemoveLoopBasedDisplayLogic={() => {
              handleRemoveLoopBasedLogic()
            }}
          />
        }
        titleContent={titleContent}
      />
      {displayLogicDialogOpen && (
        <DialogAddDisplayLogic
          entryPosition={position}
          contextPosition={contextPosition}
          open={displayLogicDialogOpen}
          onClose={() => {
            setDisplayLogicDialogOpen(false)
          }}
          isSaving={loading}
          onSave={(
            entry,
            clauseBlockContainer: ClauseBlockContainer[],
            operator: Operator,
            forkTags: ForkTag[]
          ) => {
            handleOnSave(entry, clauseBlockContainer, operator, forkTags)
          }}
        />
      )}
      {loopSpecificDisplayLogicDialogOpen && (
        <DialogAddLoopSpecificDisplayLogic
          sectionId={sectionId}
          entryId={entryId}
          contextPosition={contextPosition}
          open={loopSpecificDisplayLogicDialogOpen}
          onClose={() => {
            setLoopSpecificDisplayLogicDialogOpen(false)
          }}
          onSave={setLoopSpecificDisplayLogic}
          isSaving={loading}
        />
      )}
    </>
  )
}
export default memo(SurveyQuestionCardHeaderContainer)
